<template>
  <div class="order-header-wrapper">
    <div class="order-header-left">
      <ulu-logo :brand="order.brand" class="order-logo" />
      <div class="order-info">
        <h2 class="order-title">
          <router-link :to="{ name: 'customer', params: { id: customer.id } }" class="customer-name-link">
            {{ customer.company_name }}{{ customer.company_country ? ', ' + customer.company_country : '' }}
          </router-link>
        </h2>
        <div class="contact-info">
          <div v-if="customer.contact_first_name || customer.contact_last_name">
            {{ customer.contact_first_name }} {{ customer.contact_last_name }}{{ customer.contact_email ? ',' : '' }}
          </div>
          <div v-if="customer.contact_email">{{ customer.contact_email }}</div>
          <span
            v-if='order.cancell_at && order.cancell_at !== null && order.cancell_at !== ""'
            v-b-tooltip.hover
            :title="$t('status.' + order.cancell_type)"
            class='cancel-badge'
          >
            <b-badge>
              {{ $t("globals.cancel_at") + ': ' + order.cancell_at.substring(0, 10) }}
            </b-badge>
          </span>
        </div>
      </div>
    </div>

    <div class="order-header-right">
      <button
        v-if="
          order.status === 'created' || order.status === 'devices_imported'
        "
        @click="complete"
        class="complete-btn"
        :disabled="!getUserBrandPermissions.order_complete"
      >
        {{ $t("globals.approve") }}
      </button>
      <b-dropdown
        size="sm"
        right
        variant="outline-secondary"
        :text="$t('globals.more')"
        class="more-dropdown"
      >
        <b-dropdown-item
          v-if="
            order.status === 'completed' &&
              getUserBrandPermissions.order_terminate
          "
          @click="showTerminateModal = true"
          variant="danger"
        >
          {{ $t("globals.terminate") }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="getUserBrandPermissions.order_cancel && order.cancell_at == null && order.status === 'created'"
          @click="showCancellModal = true"
          variant="danger"
        >
          {{ $t("globals.cancel_order") }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="getUserBrandPermissions.order_cancel && order.send_invoices === true "
          @click="changeSendInvoices(true)"
          variant="danger"
        >
          {{ $t("globals.disable_sending_invoices")}}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="getUserBrandPermissions.order_cancel && order.send_invoices === false "
          @click="changeSendInvoices(false)"
          variant="danger"
        >
          {{ $t("globals.enable_sending_invoices")}}

        </b-dropdown-item>
        <b-dropdown-item
          v-if='showCancelCancellationBtn'
          @click="cancelCancellation"
          variant="info"
        >
          {{ $t("globals.cancel_cancellation") }}
        </b-dropdown-item>
        <div v-b-tooltip.hover :title="hasRequestSynatecOrders ? $t('globals.cannot_delete_order_with_requested_synatec_orders') : null">
          <b-dropdown-item
            v-if="deleteBtnShow"
            variant="danger"
            @click="deleteOrderModal.show = true"
            :disabled="hasRequestSynatecOrders"
          >
            {{ $t("globals.hard_delete") }}
          </b-dropdown-item>
        </div>
        <!-- with admin permission -->
        <b-dropdown-divider
          v-if="getUserBrandPermissions.super_admin"
        ></b-dropdown-divider>
        <b-dropdown-item
          v-if="getUserBrandPermissions.super_admin"
          variant="info"
          @click="showChangeAgentModal = true"
        >
          {{ $t("globals.change_agent") }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="getUserBrandPermissions.super_admin"
          variant="info"
          @click="showChangePaymentTypeModal = true"
        >
          {{ $t("globals.change_payment_type") }}
        </b-dropdown-item>
      </b-dropdown>
    </div>
    <b-modal
      centered
      :visible="deleteOrderModal.show"
      hide-footer
      hide-header
      no-close-on-backdrop
      no-close-on-esc
    >
      <b-container>
        <b-row>
          <b-col>
            <h4>{{ $t("globals.delete") + " " + $t("globals.order") }}</h4>
          </b-col>
        </b-row>
        <hr />
        <b-row>
          <b-col>
            <b-form-checkbox
              v-model="deleteOrderModal.delete_inquiry"
              :value="true"
              :unchecked-value="false"
            >
              {{ $t("globals.delete_also_inquiry") }}
            </b-form-checkbox>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <h6 class="text-secondary m-2">
              {{ "*" + $t("globals.left_unchecked_pending") }}
            </h6>
          </b-col>
        </b-row>
        <hr />
        <b-row>
          <b-col>
            <b-btn size="sm" variant="success" @click="deleteOrder">
              {{ $t("globals.delete") }}
            </b-btn>
          </b-col>
          <b-col>
            <b-btn
              class="float-right"
              size="sm"
              @click="deleteOrderModal.show = false"
              variant="warning"
            >
              {{ $t("globals.cancel") }}
            </b-btn>
          </b-col>
        </b-row>
      </b-container>
    </b-modal>
    <ulu-change-agent-modal
      :show="showChangeAgentModal"
      :close="closeModals"
    />
    <ulu-change-payment-type-modal
      :show="showChangePaymentTypeModal"
      :close="closeModals"
    />
    <ulu-cancell-modal
      :show="showCancellModal"
      :close="closeModals"
    />
    <ulu-terminate-modal
      :show="showTerminateModal"
      :close="closeModals"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  components: {
    "ulu-logo": () => import("@/components/common/Logo.vue"),
    "ulu-change-agent-modal": () =>
      import("@/components/orders/order/order_header/ChangeAgentModal"),
    "ulu-change-payment-type-modal": () =>
      import("@/components/orders/order/order_header/ChangePaymentTypeModal"),
    "ulu-cancell-modal": () =>
      import("@/components/orders/order/order_header/CancellModal"),
    "ulu-terminate-modal": () =>
      import("@/components/orders/order/order_header/TerminateModal")
  },
  props: {
    order: {
      type: Object,
      default: () => {
        return {
          order_no: 0,
          brand: "",
          status: "",
          send_invoices: true,
          inquiry: {
            inquiry_no: 0,
            id: 0
          },
          synatec_orders: []
        };
      }
    },
    customer: {
      type: Object,
      default: () => {
        return {
          id: 0,
          company_name: "",
          company_country: "",
          contact_first_name: "",
          contact_last_name: "",
          contact_email: ""
        };
      }
    },
    refreshOrder: Function
  },
  data() {
    return {
      showChangeAgentModal: false,
      showChangePaymentTypeModal: false,
      showCancellModal: false,
      showTerminateModal: false,
      showMakeSynatecOrderModal: false,
      deleteOrderModal: {
        show: false,
        delete_inquiry: false
      },
    };
  },
  methods: {
    ...mapActions("axios_mod", ["simpleMutation"]),
    closeModals() {
      this.showChangePaymentTypeModal = false;
      this.showChangeAgentModal = false;
      this.showCancellModal = false;
      this.showTerminateModal = false;
      this.showMakeSynatecOrderModal = false;

      this.refreshOrder();
    },
    toInquiry() {
      if (this.order.inquiry) {
        this.$router.push({
          name: "inquiry",
          params: { id: this.order.inquiry.id }
        });
      }
    },
    complete() {
      let data = {
        query: `mutation { res: OrderComplete (order_id: ${parseInt(
          this.$route.params.id
        )} ) }`
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.refreshOrder();
      });
    },
    terminate() {
      let confirmText = "Are you sure you want to terminate this order?";
      confirmText += " *All ACTIVE devices will be terminated!*";

      let data = {
        query: `
          mutation { 
            res: OrderTerminate(order_id: ${parseInt(this.$route.params.id)} )
          }
        `
      };

      this.simpleMutation({
        data,
        this_ref: this,
        confirm_text: confirmText
      }).then(() => {
        this.refreshOrder();
      });
    },
    changeSendInvoices(current_state_of_sending) {

      let confirmText = "Are you sure you want to change invoice scheduling for this order?";
      if(current_state_of_sending == true) {
        confirmText += " *All Future chedules will be canceled!*";
      } else {
        confirmText += " *Future schedules will be enabled again or created!*";
      }


      let data = {
        query: `
          mutation {
            res: OrderChangeSendInvoices(order_id: ${parseInt(this.$route.params.id)} )
          }
        `
      };

      this.simpleMutation({
        data,
        this_ref: this,
        confirm_text: confirmText
      }).then(() => {
        this.refreshOrder();
      });
    },
    cancelOrder() {
      let confirm_text = "Are you sure you want to cancel this order?";

      let data = {
        query: `
          mutation { 
            res: OrderCancel(order_id: ${parseInt(this.$route.params.id)} )
          }
        `
      };

      this.simpleMutation({ data, this_ref: this, confirm_text })
        .then(() => {
          this.refreshOrder();
        });
    },
    cancelCancellation() {
      let confirm_text = "Are you sure you want to cancel the cancellation?";

      let data = {
        query: `
          mutation { 
            res: OrderCancelCancellation (
              order_id: ${parseInt(this.$route.params.id)}
            )
          }
        `
      };

      this.simpleMutation({ data, this_ref: this, confirm_text })
        .then(() => { this.refreshOrder(); });
    },
    deleteOrder() {
      let confirm_text = "Are you sure you want to delete this order? (This action can NOT be reversed)";

      let data = {
        query: `
          mutation {
            res: OrderDelete(
              order_id: ${parseInt(this.$route.params.id)},
              delete_inquiry: ${this.deleteOrderModal.delete_inquiry}
            )
          }
        `
      };

      this.simpleMutation({ data, this_ref: this, confirm_text }).then(() => {
        this.deleteOrderModal.show = false;
        if (this.deleteOrderModal.delete_inquiry) {
          this.$router.push({ name: "allinquiries" });
        } else if (this.order.inquiry) {
          this.$router.push({
            name: "inquiry",
            params: { id: this.order.inquiry.id }
          });
        } else {
          this.$router.push({ name: "allorders" });
        }
      });
    }
  },
  computed: {
    ...mapGetters(["getUserBrandPermissions"]),
    deleteBtnShow() {
      return (
        this.order.status != "" &&
        this.order.status != "completed" &&
        this.order.status != "terminated" &&
        this.order.status != "canceled" &&
        this.getUserBrandPermissions.order_uncomplete_delete
      );
    },
    showCancelCancellationBtn() {
      return (
        this.getUserBrandPermissions.order_cancel
        && this.order.cancell_at !== null
        && this.order.status === 'completed'
      );
    },
    hasRequestSynatecOrders() {
      if (!this.order?.synatec_orders?.length) {
        return false;
      }

      return this.order.synatec_orders.some(
        so => so.status === "requested"
      );
    }
  }
};
</script>

<style scoped>
.order-header-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  width: 100%;
}

.order-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
}

.order-logo {
  flex-shrink: 0;
}

.order-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.order-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.customer-name-link {
  color: #2d3748;
  text-decoration: none;
}

.customer-name-link:hover {
  color: #17a2b8;
  text-decoration: none;
}

.contact-info {
  font-size: 14px;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.inquiry-info {
  font-size: 14px;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 8px;
}

.inquiry-link {
  color: #17a2b8;
  text-decoration: none;
}

.inquiry-link:hover {
  text-decoration: underline;
}

.cancel-badge {
  margin-left: 8px;
}

.order-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
}

.order-header-right >>> .dropdown,
.order-header-right >>> .btn {
  margin: 0 !important;
}

.complete-btn {
  padding: 6px 16px;
  background-color: #388e3c;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.complete-btn:hover:not(:disabled) {
  background-color: #2e7d32;
}

.complete-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.more-dropdown >>> .btn {
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s;
}

@media (max-width: 768px) {
  .order-header-wrapper {
    flex-direction: column;
    align-items: flex-start;
  }

  .order-header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .order-header-right {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
